import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import SubHero from '../other/SubHero';

const TermsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0rem 2rem;
`;

const Section = styled.section<{ isVisible: boolean }>`
  margin-bottom: 3rem;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.7s ease,
    transform 0.8s ease;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) =>
    props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 300;
  margin-bottom: 1.5rem;
  font-family: 'Garamond', serif;
  color: #13182a;
  position: relative;
  display: inline-block;
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -2px;
    left: 0;
    background-color: #13182a;
    transition: width 0.3s ease;
    height: 1.5px;
  }
  &:hover:after {
    width: 100%;
  }
`;
const Paragraph = styled.p`
  font-size: 1.125rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  color: #333;
  font-family: 'Garamond', sans-serif;
`;

const Separator = styled.hr`
  border: none;
  height: 1px;
  background: linear-gradient(to right, transparent, #13182a, transparent);
  margin: 2rem 0;
`;

const TermsPage: React.FC = () => {
  // Load Inter font
  useEffect(() => {
    const link = document.createElement('link');
    link.href =
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <TermsContainer>
      <SubHero title="Terms" />

      <Helmet>
        <title>Heaviside | Terms of Service</title>
        <meta name="description" content="Terms of Service for Heaviside" />
        <link rel="canonical" href="https://heaviside.com/terms" />
      </Helmet>

      <SectionWithVisibility>
        <SectionTitle>Terms</SectionTitle>
        <Paragraph>
          By accessing or using the services provided by Heaviside, you agree to
          be bound by these Terms of Service. If you do not agree to these
          terms, please do not use our services.
        </Paragraph>
      </SectionWithVisibility>

      <Separator />

      <SectionWithVisibility>
        <SectionTitle>Use of Services</SectionTitle>
        <Paragraph>
          You agree to use our services only for lawful purposes and in
          accordance with these terms. You are responsible for maintaining the
          confidentiality of your account information and for all activities
          that occur under your account.
        </Paragraph>
      </SectionWithVisibility>

      <Separator />

      <SectionWithVisibility>
        <SectionTitle>Limitation of Liability</SectionTitle>
        <Paragraph>
          Heaviside shall not be liable for any indirect, incidental, or
          consequential damages arising from your use of our services. Our
          liability is limited to the maximum extent permitted by law.
        </Paragraph>
      </SectionWithVisibility>

      <Separator />

      <SectionWithVisibility>
        <SectionTitle>Changes to Terms</SectionTitle>
        <Paragraph>
          We reserve the right to modify these terms at any time. Any changes
          will be effective immediately upon posting on our website. Your
          continued use of our services constitutes acceptance of the updated
          terms.
        </Paragraph>
      </SectionWithVisibility>
    </TermsContainer>
  );
};

// Wrapper component to handle visibility for fade-in animation
const SectionWithVisibility: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <Section ref={ref} isVisible={inView}>
      {children}
    </Section>
  );
};

export default TermsPage;
